import {RefractoryClassificationClass} from '@domain/refractory-product-catalog'

import {Refractory} from '@refra/modules/refra-catalog/types'
import {StaticPartLayerTypes} from '@refra/modules/refra-static-parts/enums'
import {StaticMaterialInstallationDTO} from '@refra/modules/refra-static-parts/types'

export interface ReplacementLayerRow {
  layer: StaticPartLayerTypes
  material: string
  thickness: number | string
  weight: number | string
}

export interface ReplacementAreaTotals {
  index: number
  totalArea: number
  totalWeight: number
  layers: ReplacementLayerRow[]
}

export interface ReplacementTotals {
  areas: ReplacementAreaTotals[]
  totalArea: number
  totalWeight: number
}

export const computeLayerWeight = (
  refractory: Refractory | undefined,
  thickness: number | undefined,
  area: number
): number => {
  if (!refractory || !thickness || !area) return 0
  const isBrick = refractory.classification?.class === RefractoryClassificationClass.Brick
  if (isBrick) return 0
  const density = refractory.physicalData?.BulkDensity?.[0]?.metric
  if (!density) return 0
  const thicknessMeters = thickness / 1000
  return Number((thicknessMeters * density * area).toFixed(2))
}

/**
 * Pure computation of per-area and grand totals for a static part replacement.
 * Shared by the live shutdown detail view and the printout so both stay in sync.
 */
export const computeReplacementTotals = (
  materialInstallations: StaticMaterialInstallationDTO[],
  refractories: Refractory[]
): ReplacementTotals => {
  const areas = materialInstallations.map<ReplacementAreaTotals>((installation, index) => {
    const totalArea = installation.sections?.reduce((acc, section) => acc + section.area, 0) ?? 0

    const layers = (installation.layers ?? []).map<ReplacementLayerRow>((layerInfo) => {
      const refractory = layerInfo.refractoryId
        ? refractories.find((r) => r.id === layerInfo.refractoryId)
        : undefined
      const thickness = layerInfo.bricksInfo?.thickness
      const weight = computeLayerWeight(refractory, thickness, totalArea)

      return {
        layer: layerInfo.layer,
        material: refractory?.name ?? '-',
        thickness: thickness ?? '-',
        weight: weight || '-'
      }
    })

    const totalWeight = layers.reduce(
      (acc, row) => acc + (typeof row.weight === 'number' ? row.weight : 0),
      0
    )

    return {index, totalArea, totalWeight, layers}
  })

  return {
    areas,
    totalArea: areas.reduce((acc, area) => acc + area.totalArea, 0),
    totalWeight: areas.reduce((acc, area) => acc + area.totalWeight, 0)
  }
}
